import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  Wallet, Shield, TrendingUp, CreditCard, Coins, Users,
  ArrowRight, Copy, CheckCircle, ExternalLink, BarChart3,
  BookOpen, Award, LogOut, Scale, Link2, Gift, Send, History, Percent, Landmark, Car, Home, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import WalletConnectModal from '../components/WalletConnectModal';
import WalletMenu from '../components/WalletMenu';
import ContactBanker from '../components/ContactBanker';
import ChainlinkPriceOracle from '../components/ChainlinkPriceOracle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token, logout } = useAuth();
  const { 
    account, 
    bethBalance, 
    availableBalance, 
    pledgedBalance,
    walletType,
    isConnecting,
    disconnectWallet
  } = useWeb3();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [collateralPosition, setCollateralPosition] = useState(null);
  const [showContactBanker, setShowContactBanker] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [token, navigate]);

  // Fetch collateral position when wallet is connected
  useEffect(() => {
    if (account && token) {
      fetchCollateralPosition();
    }
  }, [account, token]);

  const fetchCollateralPosition = async () => {
    if (!account) return;
    try {
      const res = await fetch(`${API}/collateral/position/${account}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCollateralPosition(data);
      }
    } catch (error) {
      console.error('Error fetching collateral position:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [profileRes, statsRes, priceRes, valueLockRes] = await Promise.all([
        fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/defi/protocol-stats`),
        fetch(`${API}/defi/eth-price`),
        fetch(`${API}/value-lock/summary`)
      ]);

      const profile = profileRes.ok ? await profileRes.json() : null;
      const stats = statsRes.ok ? await statsRes.json() : null;
      const price = priceRes.ok ? await priceRes.json() : null;
      const valueLock = valueLockRes.ok ? await valueLockRes.json() : null;

      setDashboardData({
        profile,
        stats,
        ethPrice: price?.price || 3500,
        valueLock
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success('Address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleConnectWallet = () => {
    setShowWalletModal(true);
  };

  const formatBalance = (balance) => {
    const num = parseFloat(balance);
    if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#b8860b]"></div>
      </div>
    );
  }

  const walletAddress = account || dashboardData?.profile?.wallet_address || '';
  const shortAddress = walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : 'Not Connected';

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="user-dashboard">
      {/* Compliance Banner */}
      <div className="bg-[#b8860b]/10 border-b border-[#b8860b]/20">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2">
          <Scale className="w-4 h-4 text-[#b8860b]" />
          <span className="text-[#b8860b] text-sm font-medium">
            Hybrid Seed Capital System • Reg D 506(b) Compliant
          </span>
        </div>
      </div>

      {/* Header - JP Morgan Style */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#b8860b] flex items-center justify-center">
              <span className="text-white font-bold text-xl">B</span>
            </div>
            <div>
              <span className="text-white font-bold text-lg tracking-wide">BETHEREUM</span>
              <p className="text-[#b8860b] text-xs tracking-widest">PRIVATE WEALTH MANAGEMENT</p>
            </div>
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/subscribe" className="text-[#b8860b] hover:text-[#d4a10a] text-sm font-medium">Subscribe</Link>
            <Link to="/credit-facility" className="text-gray-400 hover:text-white text-sm">Credit Facility</Link>
            <Link to="/credit-enhancement" className="text-gray-400 hover:text-white text-sm">Credit Enhancement</Link>
            <Link to="/onboarding" className="text-gray-400 hover:text-white text-sm">Apply</Link>
            <Link to="/rewards" className="text-gray-400 hover:text-white text-sm">Rewards</Link>
            <Link to="/compliance" className="text-gray-400 hover:text-white text-sm">Compliance</Link>
            {user?.is_admin && (
              <Link to="/admin-dashboard" className="text-[#b8860b] hover:text-[#d4a10a] text-sm font-medium">Admin</Link>
            )}
          </nav>

          <div className="flex items-center gap-4">
            {account ? (
              <WalletMenu onSwitchWallet={() => setShowWalletModal(true)} />
            ) : (
              <Button 
                onClick={handleConnectWallet} 
                disabled={isConnecting}
                className="bg-[#b8860b] hover:bg-[#9a7209]"
                size="sm"
                data-testid="connect-wallet-btn"
              >
                <Link2 className="w-4 h-4 mr-2" />
                {isConnecting ? 'Connecting...' : 'Connect Wallet'}
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowContactBanker(true)} 
              className="border-[#b8860b] text-[#b8860b] hover:bg-[#b8860b]/10"
              data-testid="contact-banker-btn"
            >
              <User className="w-4 h-4 mr-2" />
              Contact Banker
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-[#1a2d4a]" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Contact Banker Modal */}
      <ContactBanker isOpen={showContactBanker} onClose={() => setShowContactBanker(false)} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">
            Welcome back, {dashboardData?.profile?.username || user?.email?.split('@')[0] || 'Client'}
          </h2>
          <p className="text-gray-400">Manage your credit facility and track your portfolio</p>
        </div>

        {/* Wallet Connection Banner */}
        {!account && (
          <Card className="mb-8 bg-[#b8860b]/10 border-[#b8860b]/30">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Wallet className="w-6 h-6 text-[#b8860b]" />
                  <div>
                    <p className="text-white font-medium">Connect your wallet</p>
                    <p className="text-sm text-gray-400">Connect MetaMask, Trust Wallet or any WalletConnect compatible wallet</p>
                  </div>
                </div>
                <Button 
                  onClick={handleConnectWallet}
                  disabled={isConnecting}
                  className="bg-[#b8860b] hover:bg-[#9a7209]"
                >
                  {isConnecting ? 'Connecting...' : 'Connect Now'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Portfolio Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <p className="text-gray-400 text-sm">ETH Price</p>
              </div>
              <p className="text-2xl font-bold text-white">${dashboardData?.ethPrice?.toLocaleString() || '3,500'}</p>
              <p className="text-xs text-green-400 mt-1">Live Price</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Coins className="w-5 h-5 text-[#b8860b]" />
                <p className="text-gray-400 text-sm">BETH Balance</p>
              </div>
              <p className="text-2xl font-bold text-white">
                {account ? formatBalance(bethBalance) : '—'}
              </p>
              <p className="text-xs text-gray-500 mt-1">{account ? 'Your total BETH' : 'Connect wallet to view'}</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-5 h-5 text-blue-400" />
                <p className="text-gray-400 text-sm">Available to Pledge</p>
              </div>
              <p className="text-2xl font-bold text-white">
                {account ? formatBalance(availableBalance) : '—'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Unpledged BETH</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 className="w-5 h-5 text-purple-400" />
                <p className="text-gray-400 text-sm">Pledged as Collateral</p>
              </div>
              <p className="text-2xl font-bold text-white">
                {account ? formatBalance(pledgedBalance) : '—'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Locked in vault</p>
            </CardContent>
          </Card>
        </div>

        {/* My Collateral Position Card - Only show when wallet connected */}
        {account && collateralPosition && (
          <Card className="mb-8 bg-gradient-to-r from-[#0d1d35] to-[#1a2d4a] border-[#b8860b]/30" data-testid="collateral-position-card">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#b8860b]/20 flex items-center justify-center">
                    <CreditCard className="w-7 h-7 text-[#b8860b]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#b8860b] tracking-widest mb-1">YOUR CREDIT FACILITY POSITION</p>
                    <p className="text-2xl font-bold text-white">
                      {formatBalance(collateralPosition.collateral_amount)} BETH Collateral
                    </p>
                    <p className="text-sm text-gray-400">
                      {collateralPosition.is_active ? 'Active collateral position' : 'No active position - deposit to get started'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 text-center">
                  <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                    <p className="text-lg font-bold text-blue-400">{formatBalance(collateralPosition.collateral_amount)}</p>
                    <p className="text-xs text-gray-500">Deposited</p>
                  </div>
                  <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                    <p className="text-lg font-bold text-purple-400">{formatBalance(collateralPosition.credit_utilized)}</p>
                    <p className="text-xs text-gray-500">Credit Drawn</p>
                  </div>
                  <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                    <p className={`text-lg font-bold ${collateralPosition.current_ltv > 60 ? 'text-red-400' : collateralPosition.current_ltv > 40 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {collateralPosition.current_ltv}%
                    </p>
                    <p className="text-xs text-gray-500">Current LTV</p>
                  </div>
                  <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                    <p className="text-lg font-bold text-green-400">{formatBalance(collateralPosition.max_borrowable)}</p>
                    <p className="text-xs text-gray-500">Available Credit</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Platform Value Lock Security Banner */}
        <Card className="mb-8 bg-gradient-to-r from-[#0d1d35] to-[#1a2d4a] border-[#b8860b]/30" data-testid="value-lock-banner">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#b8860b]/20 flex items-center justify-center">
                  <Shield className="w-7 h-7 text-[#b8860b]" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-[#b8860b] tracking-widest">PLATFORM SECURITY</p>
                    <span className={`px-2 py-0.5 text-xs rounded ${dashboardData?.valueLock?.is_live ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {dashboardData?.valueLock?.is_live ? 'LIVE' : 'TARGET'}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {dashboardData?.valueLock ? formatBalance(dashboardData.valueLock.total_locked_beth) : '100B'} BETH Value Locked
                  </p>
                  <p className="text-sm text-gray-400">Internal collateral reserve securing all platform operations</p>
                </div>
              </div>
              <div className="flex gap-4 text-center">
                <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                  <p className="text-lg font-bold text-blue-400">50B</p>
                  <p className="text-xs text-gray-500">Collateral Vault</p>
                </div>
                <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                  <p className="text-lg font-bold text-green-400">30B</p>
                  <p className="text-xs text-gray-500">Treasury Reserve</p>
                </div>
                <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                  <p className="text-lg font-bold text-[#b8860b]">20B</p>
                  <p className="text-xs text-gray-500">Platform Reserve</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Price Oracle */}
        <div className="mb-8">
          <ChainlinkPriceOracle />
        </div>

        {/* Main Actions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Credit Facility Card */}
          <Card className="bg-[#0d1d35] border-[#1a2d4a] lg:col-span-2">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#b8860b]/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-[#b8860b]" />
                </div>
                <div>
                  <CardTitle className="text-white">Credit Facility</CardTitle>
                  <CardDescription>Access credit by pledging BETH as collateral</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-[#1a2d4a]/50 rounded-lg">
                  <p className="text-2xl font-bold text-[#b8860b]">75%</p>
                  <p className="text-xs text-gray-500">Max LTV</p>
                </div>
                <div className="text-center p-4 bg-[#1a2d4a]/50 rounded-lg">
                  <p className="text-2xl font-bold text-white">6-10%</p>
                  <p className="text-xs text-gray-500">APR Range</p>
                </div>
                <div className="text-center p-4 bg-[#1a2d4a]/50 rounded-lg">
                  <p className="text-2xl font-bold text-white">$2M</p>
                  <p className="text-xs text-gray-500">Max Credit</p>
                </div>
              </div>
              <Button 
                className="w-full bg-[#b8860b] hover:bg-[#9a7209]"
                onClick={() => navigate('/credit-facility')}
              >
                Access Credit Facility
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start bg-[#b8860b]/10 border-[#b8860b]/30 hover:bg-[#b8860b]/20 text-[#b8860b]"
                onClick={() => navigate('/subscribe')}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Subscribe - Get BETH Credits
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20 text-blue-400"
                onClick={() => navigate('/transfer')}
              >
                <Send className="w-4 h-4 mr-2" />
                Transfer BETH
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-[#b8860b]/10 border-[#b8860b]/30 hover:bg-[#b8860b]/20 text-[#b8860b]"
                onClick={() => navigate('/rewards')}
              >
                <Gift className="w-4 h-4 mr-2" />
                Claim Rewards
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start border-[#1a2d4a] text-gray-300"
                onClick={() => navigate('/my-transactions')}
              >
                <History className="w-4 h-4 mr-2 text-purple-400" />
                Transaction History
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start border-[#1a2d4a] text-gray-300"
                onClick={() => navigate('/my-addresses')}
              >
                <Wallet className="w-4 h-4 mr-2 text-green-400" />
                Address Book
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-400"
                onClick={() => navigate('/portfolio')}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Portfolio Summary
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 text-purple-400"
                onClick={() => navigate('/treasury-securities')}
                data-testid="treasury-obligations-link"
              >
                <Landmark className="w-4 h-4 mr-2" />
                Treasury-Indexed Obligations
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/20 text-cyan-400"
                onClick={() => navigate('/new-auto-financing')}
                data-testid="new-auto-financing-link"
              >
                <Car className="w-4 h-4 mr-2" />
                New Auto Finance (TIAO)
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 text-purple-400"
                onClick={() => navigate('/auto-refinance')}
                data-testid="auto-refinance-link"
              >
                <Car className="w-4 h-4 mr-2" />
                Auto Loan Refinance
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-indigo-500/10 border-indigo-500/30 hover:bg-indigo-500/20 text-indigo-400"
                onClick={() => navigate('/new-mortgage-financing')}
                data-testid="new-mortgage-financing-link"
              >
                <Home className="w-4 h-4 mr-2" />
                New Mortgage Finance (TIMO)
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 text-purple-400"
                onClick={() => navigate('/mortgage-refinance')}
                data-testid="mortgage-refinance-link"
              >
                <Home className="w-4 h-4 mr-2" />
                Mortgage Refinance
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start border-[#1a2d4a] text-gray-300"
                onClick={() => navigate('/buy-tokens')}
              >
                <Coins className="w-4 h-4 mr-2 text-[#b8860b]" />
                Get BETH Tokens
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start border-[#1a2d4a] text-gray-300"
                onClick={() => navigate('/investor-guide')}
              >
                <BookOpen className="w-4 h-4 mr-2 text-blue-400" />
                Investor Guide
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start border-[#1a2d4a] text-gray-300"
                onClick={() => navigate('/compliance')}
              >
                <Scale className="w-4 h-4 mr-2 text-green-400" />
                View Compliance
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Treasury-Indexed Obligations Feature Card */}
        <Card className="mb-8 bg-gradient-to-r from-purple-900/30 to-[#0d1d35] border-purple-500/30" data-testid="treasury-obligations-card">
          <CardContent className="py-6">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Landmark className="w-8 h-8 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-purple-400 tracking-widest mb-1">NEW PRODUCT</p>
                  <p className="text-2xl font-bold text-white">Treasury-Indexed Private Obligations</p>
                  <p className="text-sm text-gray-400">Short, medium & long-term obligations indexed to U.S. Treasury rates with BETH credit enhancement</p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                    <p className="text-lg font-bold text-green-400">4.25%</p>
                    <p className="text-xs text-gray-500">30-Day</p>
                  </div>
                  <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                    <p className="text-lg font-bold text-blue-400">5.50%</p>
                    <p className="text-xs text-gray-500">90-Day</p>
                  </div>
                  <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                    <p className="text-lg font-bold text-purple-400">6.75%</p>
                    <p className="text-xs text-gray-500">180-Day</p>
                  </div>
                  <div className="px-4 py-2 bg-[#0a1628] rounded-lg">
                    <p className="text-lg font-bold text-[#b8860b]">8.00%</p>
                    <p className="text-xs text-gray-500">365-Day</p>
                  </div>
                </div>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={() => navigate('/treasury-securities')}
                  data-testid="explore-tipo-btn"
                >
                  Explore TIPO
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-yellow-400 mt-4 text-center">⚠️ These are private contractual obligations, NOT U.S. Government securities. Treasury rates used as reference index only.</p>
          </CardContent>
        </Card>

        {/* Platform Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Credit Facility Info */}
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-2">
                <CreditCard className="w-6 h-6 text-purple-400" />
              </div>
              <CardTitle className="text-white">Credit Enhancement</CardTitle>
              <CardDescription>Collateralized credit access</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Pledge BETH as collateral
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Up to 75% loan-to-value
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Institutional-grade terms
                </li>
              </ul>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-[#1a2d4a]"
                onClick={() => navigate('/defi')}
              >
                Access Credit
              </Button>
            </CardContent>
          </Card>

          {/* For Investors */}
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-2">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <CardTitle className="text-white">Accredited Investors</CardTitle>
              <CardDescription>Reg D 506(b) compliant</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  SEC accreditation required
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Collateral-backed exposure
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Full compliance framework
                </li>
              </ul>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-[#1a2d4a]"
                onClick={() => navigate('/onboarding')}
              >
                Apply Now
              </Button>
            </CardContent>
          </Card>

          {/* Securities Lending & Repo */}
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center mb-2">
                <Wallet className="w-6 h-6 text-cyan-400" />
              </div>
              <CardTitle className="text-white">Securities Lending</CardTitle>
              <CardDescription>Loan & Repo Agreements</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  BETH-backed loans
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Repurchase agreements
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  On-chain settlement
                </li>
              </ul>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-[#1a2d4a]"
                onClick={() => navigate('/securities-transactions')}
                data-testid="transactions-btn"
              >
                Manage Transactions
              </Button>
            </CardContent>
          </Card>

          {/* Hybrid Ledger */}
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-2">
                <Shield className="w-6 h-6 text-purple-400" />
              </div>
              <CardTitle className="text-white">Hybrid Ledger</CardTitle>
              <CardDescription>On-chain/Off-chain sync</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Blockchain event sync
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  SEC Form D generation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Compliance records
                </li>
              </ul>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-[#1a2d4a]"
                onClick={() => navigate('/hybrid-ledger')}
                data-testid="hybrid-ledger-btn"
              >
                View Ledger
              </Button>
            </CardContent>
          </Card>

          {/* Resources */}
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-2">
                <BookOpen className="w-6 h-6 text-green-400" />
              </div>
              <CardTitle className="text-white">Platform Resources</CardTitle>
              <CardDescription>Guides & documentation</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Investor guide
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Compliance documents
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Smart contract docs
                </li>
              </ul>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-[#1a2d4a]"
                onClick={() => navigate('/investor-guide')}
              >
                View Resources
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Legal Footer */}
        <div className="mt-12 p-6 bg-[#0d1d35] rounded-xl border border-[#1a2d4a]">
          <p className="text-xs text-gray-500 text-center">
            <strong className="text-gray-400">Legal Notice:</strong> BETH is a utility token used for collateral, not a security. 
            Investment in Bethereum Private Credit Fund I, LP is a separate securities offering under Regulation D, Rule 506(b), 
            available only to accredited investors. Past performance does not guarantee future results.
            <Link to="/terms" className="text-[#b8860b] hover:underline ml-2">Terms of Service</Link>
            <span className="mx-2">•</span>
            <Link to="/privacy" className="text-[#b8860b] hover:underline">Privacy Policy</Link>
            <span className="mx-2">•</span>
            <Link to="/compliance" className="text-[#b8860b] hover:underline">Compliance</Link>
          </p>
        </div>
      </main>

      {/* Wallet Connect Modal */}
      <WalletConnectModal 
        isOpen={showWalletModal} 
        onClose={() => setShowWalletModal(false)} 
      />
    </div>
  );
};

export default Dashboard;
